import { inject, Injectable } from '@angular/core';
import { ControlsInterface } from '@rezevent/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  ReservationsService as ReservationsApiService,
  CreateReservationDto,
  BASE_PATH,
  ClientDto,
} from '@rezevent/api-types';

@Injectable({ providedIn: 'root' })
export class EventsService {
  private reservationsService = inject(ReservationsApiService);
  private http = inject(HttpClient);
  private basePath = inject(BASE_PATH);

  private refresh$ = new Subject<void>();

  fetch$(month?: number) {
    return this.refresh$.pipe(
      startWith({}),
      switchMap(() =>
        this.reservationsService.reservationsControllerFindAll(month)
      )
    );
  }

  update$(event: CreateReservationDto, id: string) {
    return this.reservationsService
      .reservationsControllerUpdate(id, event)
      .pipe(tap(() => this.refresh$.next()));
  }

  create$(event: CreateReservationDto) {
    return this.reservationsService
      .reservationsControllerCreate(event)
      .pipe(tap(() => this.refresh$.next()));
  }

  print$(id: string) {
    return this.http.post(
      `${this.basePath}/reservations/generate-pdf-report`,
      { id },
      { responseType: 'blob' as const }
    );
  }

  readonly hoursOptions$ = of(
    Array.from({ length: 24 }).map((_, index) => index)
  );
  readonly minutesOptions$ = of([0, 15, 30, 45]);
  readonly durationOptions$ = of(
    Array.from({ length: 12 }).map((_, i) => i * 30)
  );
}

export class EventForm {
  constructor(
    private fb: NonNullableFormBuilder,
    private defaultValue?: CreateReservationDto
  ) {
    this.startTime.valueChanges.subscribe(val => {
      if (!val) return;
      this.ctrls.startHour.setValue(val.getHours(), { emitEvent: false });
      this.ctrls.startMinute.setValue(val.getMinutes(), { emitEvent: false });
    });
  }

  startTime = this.fb.control<Date>(
    (() => {
      const d = new Date();
      d.setHours(
        this.defaultValue?.startHour ?? 0,
        this.defaultValue?.startMinute ?? 0,
        0,
        0
      );
      return d;
    })(),
    [Validators.required]
  );

  clientCtrls: ControlsInterface<ClientDto> = {
    name: this.fb.control<string>(this.defaultValue?.client?.name ?? '', [
      Validators.required,
    ]),
    phoneNumber: this.fb.control<string>(
      this.defaultValue?.client?.phoneNumber ?? '',
      [Validators.required]
    ),
    email: this.fb.control<string>(this.defaultValue?.client?.email ?? '', [
      Validators.required,
      Validators.email,
    ]),
  };

  ctrls: ControlsInterface<CreateReservationDto> = {
    venue: this.fb.control<string>(this.defaultValue?.venue ?? '', [
      Validators.required,
    ]),
    date: this.fb.control<string>(this.defaultValue?.date ?? '', [
      Validators.required,
    ]),
    totalGuests: this.fb.control<number>(this.defaultValue?.totalGuests ?? 0, [
      Validators.required,
      Validators.min(1),
    ]),
    isPaid: this.fb.control<boolean>(this.defaultValue?.isPaid ?? false),
    note: this.fb.control<string>(this.defaultValue?.note ?? ''),
    menu: this.fb.control<string>(this.defaultValue?.menu ?? '', [
      Validators.required,
    ]),
    client: this.fb.group(this.clientCtrls),
    startHour: this.fb.control<number>(this.defaultValue?.startHour ?? 0, [
      Validators.required,
    ]),
    startMinute: this.fb.control<number>(this.defaultValue?.startMinute ?? 0, [
      Validators.required,
    ]),
    duration: this.fb.control<number>(this.defaultValue?.duration ?? 60, [
      Validators.required,
    ]),
  };

  private _fg = this.fb.group(this.ctrls);

  form = {
    fg: this._fg,
    fgValueChanges$: this._fg.valueChanges.pipe(
      map(() => this._fg.getRawValue() as CreateReservationDto)
    ),
    fgValue$: this._fg.valueChanges.pipe(
      startWith(null),
      map(() => this._fg.getRawValue() as CreateReservationDto)
    ),
  };
}
